import { useQuery, useMutation, useQueryClient } from 'react-query';
import * as docflowService from '@/services/docflowService';
import type {
  IWorkflow,
  IExecution,
  IExecutionResult,
  IDriveFile,
  ITemplate,
  ICreateWorkflowRequest,
  IUpdateWorkflowRequest,
  IApprovedResult,
} from '@/types/docflow';

// ============================================================================
// Query Keys
// ============================================================================

export const DOCFLOW_QUERY_KEYS = {
  all: ['docflow'] as const,
  workflows: () => [...DOCFLOW_QUERY_KEYS.all, 'workflows'] as const,
  workflow: (id: string) => [...DOCFLOW_QUERY_KEYS.all, 'workflow', id] as const,
  executions: (workflowId: string) => [...DOCFLOW_QUERY_KEYS.all, 'executions', workflowId] as const,
  execution: (id: string) => [...DOCFLOW_QUERY_KEYS.all, 'execution', id] as const,
  executionResults: (execId: string) => [...DOCFLOW_QUERY_KEYS.all, 'results', execId] as const,
  driveFolders: (folderId: string) => [...DOCFLOW_QUERY_KEYS.all, 'driveFolders', folderId] as const,
  driveFiles: (folderId: string) => [...DOCFLOW_QUERY_KEYS.all, 'driveFiles', folderId] as const,
  templates: () => [...DOCFLOW_QUERY_KEYS.all, 'templates'] as const,
};

// ============================================================================
// Read Hooks
// ============================================================================

export const useWorkflows = () => {
  return useQuery<IWorkflow[], Error>(
    DOCFLOW_QUERY_KEYS.workflows(),
    () => docflowService.listWorkflows(),
    {
      staleTime: 30000,
      cacheTime: 300000,
    }
  );
};

export const useWorkflow = (id: string) => {
  return useQuery<IWorkflow, Error>(
    DOCFLOW_QUERY_KEYS.workflow(id),
    () => docflowService.getWorkflow(id),
    {
      enabled: !!id,
      staleTime: 30000,
    }
  );
};

export const useExecutions = (workflowId: string) => {
  return useQuery<IExecution[], Error>(
    DOCFLOW_QUERY_KEYS.executions(workflowId),
    () => docflowService.listExecutions(workflowId),
    {
      enabled: !!workflowId,
      staleTime: 10000,
    }
  );
};

export const useExecution = (id: string) => {
  return useQuery<IExecution, Error>(
    DOCFLOW_QUERY_KEYS.execution(id),
    () => docflowService.getExecution(id),
    {
      enabled: !!id,
      refetchInterval: (data) =>
        data?.status === 'running' ? 3000 : false,
    }
  );
};

export const useExecutionResults = (execId: string) => {
  return useQuery<IExecutionResult[], Error>(
    DOCFLOW_QUERY_KEYS.executionResults(execId),
    () => docflowService.getExecutionResults(execId),
    {
      enabled: !!execId,
      staleTime: 10000,
    }
  );
};

export const useDriveSubFolders = (folderId: string) => {
  return useQuery<IDriveFile[], Error>(
    DOCFLOW_QUERY_KEYS.driveFolders(folderId),
    () => docflowService.listDriveSubFolders(folderId),
    {
      enabled: !!folderId,
      staleTime: 60000,
    }
  );
};

export const useDriveFiles = (folderId: string) => {
  return useQuery<IDriveFile[], Error>(
    DOCFLOW_QUERY_KEYS.driveFiles(folderId),
    () => docflowService.listDriveFiles(folderId),
    {
      enabled: !!folderId,
      staleTime: 60000,
    }
  );
};

export const useTemplates = () => {
  return useQuery<ITemplate[], Error>(
    DOCFLOW_QUERY_KEYS.templates(),
    () => docflowService.listTemplates(),
    {
      staleTime: Infinity,
    }
  );
};

// ============================================================================
// Mutation Hooks
// ============================================================================

export const useCreateWorkflow = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: ICreateWorkflowRequest) => docflowService.createWorkflow(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(DOCFLOW_QUERY_KEYS.workflows());
      },
    }
  );
};

export const useUpdateWorkflow = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }: { id: string; data: IUpdateWorkflowRequest }) =>
      docflowService.updateWorkflow(id, data),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(DOCFLOW_QUERY_KEYS.workflows());
        queryClient.invalidateQueries(DOCFLOW_QUERY_KEYS.workflow(variables.id));
      },
    }
  );
};

export const useDeleteWorkflow = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (id: string) => docflowService.deleteWorkflow(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(DOCFLOW_QUERY_KEYS.workflows());
      },
    }
  );
};

export const useDeleteExecution = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id }: { id: string; workflowId: string }) =>
      docflowService.deleteExecution(id),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(DOCFLOW_QUERY_KEYS.executions(variables.workflowId));
      },
    }
  );
};

export const useRunWorkflow = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, fileIds }: { id: string; fileIds: string[] }) =>
      docflowService.runWorkflow(id, fileIds),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(DOCFLOW_QUERY_KEYS.executions(variables.id));
      },
    }
  );
};

export const useExtractWorkflow = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, fileIds }: { id: string; fileIds: string[] }) =>
      docflowService.extractWorkflow(id, fileIds),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(DOCFLOW_QUERY_KEYS.executions(variables.id));
      },
    }
  );
};

export const useApproveExecution = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, results }: { id: string; results: IApprovedResult[] }) =>
      docflowService.approveExecution(id, results),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(DOCFLOW_QUERY_KEYS.execution(data.id));
        queryClient.invalidateQueries(DOCFLOW_QUERY_KEYS.executionResults(data.id));
        queryClient.invalidateQueries(DOCFLOW_QUERY_KEYS.executions(data.workflow_id));
      },
    }
  );
};
